/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module carousel-add-slide/bulbimagecarouseladdslideui
 */

import { Plugin, ButtonView, type Locale } from 'ckeditor5';
import bulbAddSlideIcon from '../../../assets/plugin_icons/layer-plus-regular.svg';

export default class BulbImageCarouselAddSlideUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName() {
    return 'BulbImageCarouselAddSlideUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    const { t } = editor;
    let activeImageCarouselSlideIndex: number;

    editor.ui.componentFactory.add('bulbAddSlide', (locale: Locale): ButtonView => {
      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      buttonView.set({
        label: t('editor_image_carousel_bar_add_slide'),
        icon: bulbAddSlideIcon,
        tooltip: true,
      });

      // Callback executed once the button is clicked:
      buttonView.on('execute', (event) => {
        const selectedElement = editor.model.document.selection.getSelectedElement();
        const blockId = selectedElement.getAttribute('bulb-block-id');
        let orientation = null;
        if (blockId) {
          const figureElement = document.querySelector(`figure[bulb-block-id="${blockId}"]`);

          if (figureElement) {
            const possibleOrientations = ['FREEFORM', 'LANDSCAPE', 'PORTRAIT', 'SQUARE'];

            orientation = possibleOrientations.find((orientation) => figureElement.classList.contains(orientation));
          }
        }
        const currentIndexEvent = new CustomEvent('currentSlideIndex', {
          detail: {
            blockId,
          },
        });
        window.dispatchEvent(currentIndexEvent);

        const openAssetLibraryEvent = new CustomEvent('addImageToCarousel', {
          detail: {
            event,
            eventFiredFromEditor: true,
            blockId,
            orientation,
            imageIndex: activeImageCarouselSlideIndex,
            editor,
          },
        });

        window.dispatchEvent(openAssetLibraryEvent);
      });

      // Listen for the event that is going to be fired from our client for the current image index.
      window.addEventListener('dispatchSlideIndex', (event: any) => {
        activeImageCarouselSlideIndex = event.detail.activeIndex;
        if (event.detail.activeIndex === -1) {
          activeImageCarouselSlideIndex = 0;
        }
      });

      return buttonView;
    });
  }
}
